/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { ApiReducer, ApplicationState, UserReducer } from '../../../data/interfaces';
import { connect } from 'react-redux';
import { Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { PATH_SEPARATOR, RESOURCE_API, RESOURCE_OVERVIEW } from '../../../data/constants';
import Settings from '../../../conf/settings';
import CloseableBanner from '../../ChoreoSystem/CloseableBanner';
import { Link } from 'react-router-dom';

interface ApiLatestVersionAvailableBannerProps {
  apiReducer: ApiReducer,
  userReducer: UserReducer,
  open: boolean,
  close: () => void,
}

function ApiLatestVersionAvailableBanner(props: ApiLatestVersionAvailableBannerProps) {
  const { apiReducer, userReducer, open, close } = props;
  const { api, loading } = apiReducer;
  // Check if api is defined and has versionInfo property
  const versionInfo = api && api.versionInfo ? api.versionInfo : null;
  const { handle: orgHandle } = userReducer.selectedOrg;

  const LatestVersionAvailableBannerContents = versionInfo && (
    <Typography variant="body2">
      <FormattedMessage
        id="apis.apiLatestVersionAvailableBanner.api.latest.version.available.banner.message"
        defaultMessage="Version {newVersion} of this API is now available"
        values={{
          newVersion: (
            <Link
              to={
                PATH_SEPARATOR +
                orgHandle +
                RESOURCE_API +
                PATH_SEPARATOR +
                versionInfo?.majorRange?.latestVersionAPIId +
                RESOURCE_OVERVIEW
              }
            >
              {versionInfo?.majorRange?.latestVersion}
            </Link>
          ),
        }}
      />
    </Typography>
  );

  return (
    <CloseableBanner
      close={close}
      open={
        Settings.intelligentRoutingFeatureEnabled &&
        !loading &&
        open &&
        !!versionInfo &&
        !versionInfo.majorRange.isLatest
      }
      contents={LatestVersionAvailableBannerContents}
      notificationProps={{
        color: "warning",
        icon: "warning",
        fullWidth: true,
        children: null,
      }}
    />
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiReducer: state.apiReducer,
    userReducer: state.userReducer,
  }
}

export default connect(mapStateToProps)(ApiLatestVersionAvailableBanner);
